/*
 * Soundium "Demo · Vilnius" badge.
 *
 * PAGE context: desktop hover/focus is pure CSS (:hover / :focus-within); this
 * element only adds tap-to-toggle for touch, plus Esc / outside-click to close.
 *
 * CARD context (data-context="card"): the badge is informational and must NOT
 * navigate. The balloon is portaled to <body> so it escapes the card's
 * overflow:hidden and the hover transform that makes .product-card a containing
 * block. Open/close + fixed positioning are driven here (hover, focus, tap),
 * with a small close delay so the pointer can travel the gap into the balloon.
 */
if (!customElements.get('soundium-demo-badge')) {
  customElements.define(
    'soundium-demo-badge',
    class SoundiumDemoBadge extends HTMLElement {
      connectedCallback() {
        this.btn = this.querySelector('.snd-demo__btn');
        this.pop = this.querySelector('.snd-demo__pop');
        if (!this.btn) return;

        this.isCard = this.dataset.context === 'card';
        this._closeTimer = null;

        this._onClick = this._onClick.bind(this);
        this._onDocClick = this._onDocClick.bind(this);
        this._onKey = this._onKey.bind(this);
        this._reposition = this._reposition.bind(this);

        this.btn.addEventListener('click', this._onClick);
        this.addEventListener('keydown', this._onKey);

        if (this.isCard && this.pop) {
          // Portal the balloon out of the clipping/transformed card.
          this.pop.classList.add('snd-demo__pop--portaled');
          document.body.appendChild(this.pop);

          this._open = () => { this._clearClose(); this.open(); };
          this._scheduleClose = () => {
            this._clearClose();
            this._closeTimer = setTimeout(() => this.close(), 160);
          };

          this.addEventListener('pointerenter', this._open);
          this.addEventListener('pointerleave', this._scheduleClose);
          this.addEventListener('focusin', this._open);
          this.pop.addEventListener('pointerenter', () => this._clearClose());
          this.pop.addEventListener('pointerleave', this._scheduleClose);
        } else {
          // Page context: keep aria in sync when CSS hover/focus opens it.
          this.addEventListener('focusout', () => {
            if (!this.contains(document.activeElement)) this.close();
          });
        }
      }

      disconnectedCallback() {
        document.removeEventListener('click', this._onDocClick);
        window.removeEventListener('scroll', this._reposition, true);
        window.removeEventListener('resize', this._reposition);
        this._clearClose();
        // Remove the portaled balloon so re-rendered grids (filters, etc.)
        // don't leave orphans behind in <body>.
        if (this.isCard && this.pop && this.pop.parentNode === document.body) {
          this.pop.remove();
        }
      }

      _clearClose() {
        if (this._closeTimer) {
          clearTimeout(this._closeTimer);
          this._closeTimer = null;
        }
      }

      _onClick(e) {
        // Never let the badge navigate (card overlay link) or submit anything.
        e.preventDefault();
        e.stopPropagation();
        this.isOpen() ? this.close() : this.open();
      }

      _onDocClick(e) {
        if (!this.contains(e.target) && !(this.pop && this.pop.contains(e.target))) {
          this.close();
        }
      }

      _onKey(e) {
        if (e.key === 'Escape') {
          this.close();
          this.btn.focus();
        }
      }

      isOpen() {
        return this.isCard ? this.pop && this.pop.classList.contains('is-open')
                           : this.classList.contains('is-open');
      }

      open() {
        this.btn.setAttribute('aria-expanded', 'true');
        if (this.isCard && this.pop) {
          this._position();
          this.pop.classList.add('is-open');
          window.addEventListener('scroll', this._reposition, true);
          window.addEventListener('resize', this._reposition);
        } else {
          this.classList.add('is-open');
        }
        document.addEventListener('click', this._onDocClick);
      }

      close() {
        this._clearClose();
        this.btn.setAttribute('aria-expanded', 'false');
        if (this.isCard && this.pop) {
          this.pop.classList.remove('is-open', 'snd-demo__pop--above');
          window.removeEventListener('scroll', this._reposition, true);
          window.removeEventListener('resize', this._reposition);
        } else {
          this.classList.remove('is-open');
        }
        document.removeEventListener('click', this._onDocClick);
      }

      _reposition() {
        if (this.isOpen()) this._position();
      }

      _position() {
        const pop = this.pop;
        const r = this.btn.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const gap = 12;
        const pad = 8;
        // offsetWidth/Height are valid even while visibility:hidden (display:block).
        const pw = pop.offsetWidth || 300;
        const ph = pop.offsetHeight || 280;

        // If the badge scrolled out of view, close instead of floating.
        if (r.bottom < 0 || r.top > vh) { this.close(); return; }

        let above = false;
        let top = r.bottom + gap;
        if (top + ph > vh - pad && r.top - gap - ph > pad) {
          top = r.top - gap - ph;
          above = true;
        }

        let left = Math.min(r.left, vw - pw - pad);
        left = Math.max(pad, left);

        pop.style.top = top + 'px';
        pop.style.left = left + 'px';
        pop.classList.toggle('snd-demo__pop--above', above);

        const arrowX = Math.max(12, Math.min(r.left + r.width / 2 - left, pw - 12));
        pop.style.setProperty('--arrow-x', arrowX + 'px');
      }
    }
  );
}
